import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../utils/auth';
import { useCapability } from '../utils/capabilityContext';

const Dashboard = () => {
  const isAdminAuthenticated = Boolean(localStorage.getItem('admin_token'));
  const { currentUser, logout } = useAuth();
  const { debugEnabled, refresh: refreshCapabilities } = useCapability();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language.startsWith('zh');

  const role = isAdminAuthenticated
    ? t('dashboard.admin')
    : currentUser
      ? t('dashboard.doctorUser')
      : t('dashboard.visitor');

  const access = isAdminAuthenticated
    ? t('dashboard.doctorManagement')
    : currentUser
      ? t('dashboard.pendingReports')
      : t('dashboard.pleaseLogin');

  const identity = currentUser?.name || (isAdminAuthenticated ? t('dashboard.admin') : t('dashboard.visitor'));

  const summaryItems = [
    {
      label: isZh ? '当前身份' : 'Current role',
      value: role,
      note: isZh ? '登录后可查看对应的功能菜单。' : 'Your available features depend on your login role.'
    },
    {
      label: isZh ? '主要入口' : 'Primary queue',
      value: access,
      note: isZh ? '医生主要处理报告审核，管理员主要管理医生账号。' : 'Doctors focus on reviewing reports, while admins manage doctor accounts.'
    },
    {
      label: isZh ? '当前会话' : 'Signed in as',
      value: identity,
      note: isZh ? '可通过顶部菜单进入个人中心或退出登录。' : 'Use the menu above to visit your profile or sign out.'
    }
  ];

  const quickActions = currentUser
    ? [
        { label: t('dashboard.pendingReports'), to: '/reports', variant: 'primary' },
        { label: t('dashboard.reviewedReports'), to: '/reviewed-reports', variant: 'ghost' },
        { label: t('dashboard.userCenter'), to: '/user-center', variant: 'secondary' }
      ]
    : isAdminAuthenticated
      ? [
          { label: t('dashboard.doctorManagement'), to: '/doctors', variant: 'primary' },
          ...(debugEnabled
            ? [{ label: t('debugDemo.menuLabel'), to: '/admin/debug-demo', variant: 'ghost' }]
            : [])
        ]
      : [
          { label: t('dashboard.doctorLogin'), to: '/login', variant: 'primary' },
          { label: t('dashboard.adminLogin'), to: '/admin-login', variant: 'ghost' }
        ];

  const handleDoctorLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleAdminLogout = async () => {
    localStorage.removeItem('admin_token');
    await refreshCapabilities();
    navigate('/');
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <span className="page-eyebrow">AI Clinic</span>
          <h1 className="page-title">
            {t('dashboard.welcome', { name: currentUser ? currentUser.name : t('dashboard.visitor') })}
          </h1>
          <p className="page-subtitle">
            {isZh
              ? '在这里查看您的工作概览，快速进入常用功能。'
              : 'View your workspace overview and quickly access the tools you use most.'}
          </p>
        </div>

        <div className="page-actions">
          {isAdminAuthenticated && (
            <button
              type="button"
              className="theme-button theme-button--ghost"
              onClick={handleAdminLogout}
            >
              {t('dashboard.logoutAdmin')}
            </button>
          )}
          {currentUser && (
            <button
              type="button"
              className="theme-button theme-button--ghost"
              onClick={handleDoctorLogout}
            >
              {t('auth.logout')}
            </button>
          )}
        </div>
      </div>

      <div className="summary-grid">
        {summaryItems.map((item) => (
          <section key={item.label} className="surface-panel summary-card">
            <div className="surface-panel__body stack">
              <span className="summary-card__label">{item.label}</span>
              <span className="summary-card__value">{item.value}</span>
              <span className="summary-card__note">{item.note}</span>
            </div>
          </section>
        ))}
      </div>

      <div className="utility-grid">
        <section className="surface-panel">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <span className="page-eyebrow">
                {isZh ? 'Workspace' : 'Workspace'}
              </span>
              <h2 className="report-panel-title">
                {currentUser
                  ? t('dashboard.doctorInfo')
                  : isAdminAuthenticated
                    ? t('dashboard.adminInfo')
                    : t('dashboard.getStarted')}
              </h2>
            </div>

            {currentUser && (
              <div className="info-list">
                <div className="info-list__row">
                  <span className="info-list__label">{t('auth.name')}</span>
                  <span className="info-list__value">{currentUser.name}</span>
                </div>
                <div className="info-list__row">
                  <span className="info-list__label">{t('dashboard.jobTitle')}</span>
                  <span className="info-list__value">{currentUser.title || t('dashboard.notSet')}</span>
                </div>
                <div className="info-list__row">
                  <span className="info-list__label">{t('dashboard.specialty')}</span>
                  <span className="info-list__value">{currentUser.specialty || t('dashboard.notSet')}</span>
                </div>
                <div className="info-list__row">
                  <span className="info-list__label">{t('userCenter.licenseNumber')}</span>
                  <span className="info-list__value">{currentUser.license_number || t('dashboard.notSet')}</span>
                </div>
              </div>
            )}

            {isAdminAuthenticated && !currentUser && (
              <div className="status-banner status-banner--success">
                <span>{t('dashboard.adminLoggedIn')}</span>
              </div>
            )}

            {!currentUser && !isAdminAuthenticated && (
              <div className="status-banner">
                <span>{t('dashboard.pleaseLogin')}</span>
              </div>
            )}
          </div>
        </section>

        <section className="surface-panel surface-panel--soft">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <span className="page-eyebrow">
                {isZh ? 'Next Action' : 'Next Action'}
              </span>
              <h2 className="report-panel-title">
                {isZh ? '快速入口' : 'Quick actions'}
              </h2>
              <p className="page-subtitle">
                {isZh
                  ? '根据您的身份显示相关功能入口。'
                  : 'Showing the features available for your current role.'}
              </p>
            </div>

            <div className="stack">
              {quickActions.map((action) => (
                <Link
                  key={action.to}
                  to={action.to}
                  className={`theme-button ${
                    action.variant === 'primary'
                      ? 'theme-button--primary'
                      : action.variant === 'secondary'
                        ? 'theme-button--secondary'
                        : 'theme-button--ghost'
                  }`}
                >
                  {action.label}
                </Link>
              ))}
            </div>

            <div className="status-banner">
              <span>
                {isZh
                  ? '所有页面风格统一，方便您在不同功能间快速切换。'
                  : 'All pages share a consistent design, making it easy to navigate between features.'}
              </span>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Dashboard;
